import { Controller } from "@hotwired/stimulus"
import { Canvas, FabricImage, PencilBrush } from "fabric"

export default class extends Controller {
  static targets = ["autoSection", "manualSection", "canvas", "lineWidth", "maskImage"]
  static values = {
    originalWidth: Number,
    originalHeight: Number
  }

  connect() {
    this.hideAllSections()
    // Show auto section by default
    this.autoSectionTarget.classList.remove("hidden")

    // Listen for the custom event from dropzone
    this.dropzoneHandler = (event) => {
      if (event.detail.inputId === "base_image_input") {
        this.baseImageFile = event.detail.file
        
        // If we're in manual mode, initialize canvas and load image
        if (!this.manualSectionTarget.classList.contains("hidden")) {
          this.initializeCanvas()
          this.loadImageToCanvas(this.baseImageFile)
        }
      }
    }

    // Also listen for direct file input changes
    this.fileInputHandler = () => {
      const input = document.getElementById("base_image_input")
      if (input.files && input.files[0]) {
        this.baseImageFile = input.files[0]
        if (!this.manualSectionTarget.classList.contains("hidden")) {
          this.initializeCanvas()
          this.loadImageToCanvas(this.baseImageFile)
        }
      }
    }

    document.addEventListener("dropzone:success", this.dropzoneHandler)
    document.getElementById("base_image_input")?.addEventListener("change", this.fileInputHandler)
  }

  disconnect() {
    document.removeEventListener("dropzone:success", this.dropzoneHandler)
    document.getElementById("base_image_input")?.removeEventListener("change", this.fileInputHandler)
    if (this.canvas) {
      this.canvas.dispose()
    }
    // Remove overlay canvas
    this.overlayCanvas?.remove()
  }

  loadImageToCanvas(file) {
    if (!this.canvas) {
      this.initializeCanvas()
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const fabricImg = new FabricImage(img)
        this.setCanvasDimensions(fabricImg)
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  initializeCanvas() {
    // First destroy any existing canvas instance
    if (this.canvas) {
      this.canvas.dispose()
    }

    const container = this.canvasTarget.parentElement
    
    // Create canvas with container dimensions
    this.canvas = new Canvas(this.canvasTarget, {
      isDrawingMode: true,
      backgroundColor: "black",
      width: container.offsetWidth,
      height: container.offsetHeight,
      selection: false // Disable selection to avoid accidental moves
    })

    // Initialize brush
    this.canvas.freeDrawingBrush = new PencilBrush(this.canvas)
    this.canvas.freeDrawingBrush.color = "white"
    this.canvas.freeDrawingBrush.width = 10
    this.canvas.freeDrawingBrush.decimate = 8 // Smoothing

    // Create a separate canvas overlay for the brush preview
    const overlayCanvas = document.createElement('canvas')
    overlayCanvas.style.position = 'absolute'
    overlayCanvas.style.top = '0'
    overlayCanvas.style.left = '0'
    overlayCanvas.style.pointerEvents = 'none'
    overlayCanvas.width = container.offsetWidth
    overlayCanvas.height = container.offsetHeight
    container.appendChild(overlayCanvas)
    this.overlayContext = overlayCanvas.getContext('2d')
    this.overlayCanvas = overlayCanvas

    // Update brush preview position
    this.canvas.on("mouse:move", (evt) => {
      if (!evt.e) return
      const pointer = this.canvas.getPointer(evt.e)
      this.drawBrushPreview(pointer.x, pointer.y)
    })

    // Hide brush preview when mouse leaves canvas
    this.canvas.on("mouse:out", () => {
      this.overlayContext.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height)
    })

    // Handle window resize
    this.resizeHandler = () => this.handleResize()
    window.addEventListener("resize", this.resizeHandler)
  }

  drawBrushPreview(x, y) {
    // Clear previous preview
    this.overlayContext.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height)

    // Draw new preview
    this.overlayContext.beginPath()
    this.overlayContext.arc(x, y, this.canvas.freeDrawingBrush.width / 2, 0, Math.PI * 2)
    this.overlayContext.fillStyle = 'rgba(0, 255, 0, 0.2)'
    this.overlayContext.fill()
    this.overlayContext.strokeStyle = 'rgba(0, 255, 0, 0.8)'
    this.overlayContext.lineWidth = 1.5
    this.overlayContext.stroke()
  }

  setCanvasDimensions(img) {
    // Store the original dimensions
    this.originalWidthValue = img.width
    this.originalHeightValue = img.height

    // Get the container dimensions
    const container = this.canvasTarget.parentElement
    const containerWidth = container.offsetWidth
    const containerHeight = container.offsetHeight

    // Calculate the scaling ratios
    const widthRatio = containerWidth / img.width
    const heightRatio = containerHeight / img.height

    // Use the smaller ratio to ensure the entire image fits
    const scale = Math.min(widthRatio, heightRatio)

    // Calculate the new dimensions
    const scaledWidth = Math.round(img.width * scale)
    const scaledHeight = Math.round(img.height * scale)

    // Set the canvas dimensions to container size for letterboxing
    this.canvas.setDimensions({
      width: containerWidth,
      height: containerHeight
    }, { backstoreOnly: false })

    // Store scale factors for later use
    this.canvasScaleX = scale
    this.canvasScaleY = scale

    // Calculate centering offsets
    const leftOffset = (containerWidth - scaledWidth) / 2
    const topOffset = (containerHeight - scaledHeight) / 2

    // Apply the backgroundImage centered
    this.canvas.setBackgroundImage(img, this.canvas.renderAll.bind(this.canvas), {
      originX: "left",
      originY: "top",
      left: leftOffset,
      top: topOffset,
      scaleX: scale,
      scaleY: scale
    })

    // Store the image bounds for coordinate conversion
    this.imageBounds = {
      left: leftOffset,
      top: topOffset,
      width: scaledWidth,
      height: scaledHeight
    }

    // Update canvas offset for proper mouse coordinate calculation
    this.canvas.calcOffset()
  }

  handleResize() {
    if (this.canvas.backgroundImage) {
      this.setCanvasDimensions(this.canvas.backgroundImage._element)
      // Update overlay canvas size
      if (this.overlayCanvas) {
        const container = this.canvasTarget.parentElement
        this.overlayCanvas.width = container.offsetWidth
        this.overlayCanvas.height = container.offsetHeight
      }
    }
  }

  hideAllSections() {
    this.autoSectionTarget.classList.add("hidden")
    this.manualSectionTarget.classList.add("hidden")
  }

  maskTypeChanged(event) {
    this.hideAllSections()
    
    if (event.target.value === "auto") {
      this.autoSectionTarget.classList.remove("hidden")
    } else {
      this.manualSectionTarget.classList.remove("hidden")
      
      // Initialize canvas and load image if available
      if (this.baseImageFile) {
        this.initializeCanvas()
        this.loadImageToCanvas(this.baseImageFile)
      } else {
        const baseImageInput = document.getElementById("base_image_input")
        if (baseImageInput.files && baseImageInput.files[0]) {
          this.baseImageFile = baseImageInput.files[0]
          this.initializeCanvas()
          this.loadImageToCanvas(this.baseImageFile)
        }
      }
    }
  }

  updateBrushSize(event) {
    const size = parseInt(event.target.value)
    this.canvas.freeDrawingBrush.width = size

    // Show the brush preview in the center of the canvas
    const centerX = this.overlayCanvas.width / 2
    const centerY = this.overlayCanvas.height / 2
    this.drawBrushPreview(centerX, centerY)

    // Set a timeout to clear the centered preview after 1 second
    if (this.centerPreviewTimeout) {
      clearTimeout(this.centerPreviewTimeout)
    }
    this.centerPreviewTimeout = setTimeout(() => {
      // After timeout, if mouse is over canvas, show at mouse position, otherwise clear
      const pointer = this.canvas.getPointer(this.canvas.getActivePointer?.() || this.canvas._currentTransform?.pointer || {})
      if (pointer && pointer.x !== undefined) {
        this.drawBrushPreview(pointer.x, pointer.y)
      } else {
        this.overlayContext.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height)
      }
    }, 1000)
  }

  undo() {
    const objects = this.canvas.getObjects()
    if (objects.length > 0) {
      this.canvas.remove(objects[objects.length - 1])
      this.canvas.renderAll()
    }
  }

  clear() {
    this.canvas.getObjects().forEach(obj => {
      this.canvas.remove(obj)
    })
    this.canvas.renderAll()
  }

  // This method will be called when submitting the form
  getMaskData(event) {
    // Only process canvas data if we're in manual mode
    if (!this.manualSectionTarget.classList.contains("hidden")) {
      // Check if canvas exists and has objects
      if (!this.canvas || this.canvas.getObjects().length === 0) {
        alert("Please draw a mask first")
        event.preventDefault()
        return
      }

      // Store current canvas state
      const backgroundImage = this.canvas.backgroundImage
      const currentWidth = this.canvas.width
      const currentHeight = this.canvas.height
      const currentScale = { x: this.canvasScaleX, y: this.canvasScaleY }
      const currentBounds = { ...this.imageBounds }

      // Temporarily remove the background image
      this.canvas.setBackgroundImage(null, this.canvas.renderAll.bind(this.canvas))

      // Set canvas to original image dimensions
      this.canvas.setWidth(this.originalWidthValue)
      this.canvas.setHeight(this.originalHeightValue)

      // Scale all objects to match original image size
      const objects = this.canvas.getObjects()
      for (let obj of objects) {
        // First subtract the letterboxing offset
        const adjustedLeft = obj.left - currentBounds.left
        const adjustedTop = obj.top - currentBounds.top

        // Then scale to original dimensions
        obj.scaleX = obj.scaleX / currentScale.x
        obj.scaleY = obj.scaleY / currentScale.y
        obj.left = adjustedLeft / currentScale.x
        obj.top = adjustedTop / currentScale.y

        obj.setCoords()
      }

      // Render and get the data
      this.canvas.renderAll()
      const maskData = this.canvas.toDataURL({
        format: "png",
        enableRetinaScaling: false,
        backgroundColor: "black" // Ensure black background for the mask
      })

      // Convert base64 to blob
      const byteString = atob(maskData.split(',')[1])
      const mimeString = maskData.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: mimeString })
      const file = new File([blob], "mask.png", { type: "image/png" })

      // Create a new DataTransfer object and add our file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)

      // Set the file in the mask_image input
      this.maskImageTarget.files = dataTransfer.files

      // Restore the canvas dimensions and state
      this.canvas.setDimensions({
        width: currentWidth,
        height: currentHeight
      })

      // Restore the background image with its original settings
      this.canvas.setBackgroundImage(backgroundImage, this.canvas.renderAll.bind(this.canvas), {
        originX: "left",
        originY: "top",
        left: currentBounds.left,
        top: currentBounds.top,
        scaleX: currentScale.x,
        scaleY: currentScale.y
      })

      // Restore the scale factors
      this.canvasScaleX = currentScale.x
      this.canvasScaleY = currentScale.y
      this.imageBounds = currentBounds
    }
  }
} 
